import { ArrowDown, ArrowUp } from '@ui/components/icons';
import { PairPopover } from '@ui/features/Trade/components/TradePairPanel/components/PairPopover';
import { useIsTradePageContainerDesktop } from '@ui/features/Trade/hooks/useIsTradePageContainerDesktop';
import { Icon } from '@ui/lib/park/ui/icon';
import { useState } from 'react';
import { css } from 'styled-system/css';
import { Divider, Flex, FlexProps, HStack } from 'styled-system/jsx';

import { BorrowRates } from './components/BorrowRates';
import { CollapseStats } from './components/CollapseStats';
import { DayStats } from './components/DayStats';
import { OpenInterestsDesktop } from './components/OpenInterestsDesktop';
import { StatSectionSummary } from './components/StatSectionSummary';
import { TokenPair } from './components/TokenPair';
import { TokenPrice } from './components/TokenPrice';
import { Vol24h } from './components/Vol24h';

export const StatSection = () => {
  return (
    <>
      <Mobile />
      <Desktop />
    </>
  );
};

const Mobile = () => {
  const { value: isContainerDesktop, isPending } =
    useIsTradePageContainerDesktop();
  const [isOpen, setIsOpen] = useState(false);

  if (!isPending && isContainerDesktop) {
    return <></>;
  }

  return (
    <Flex
      display={{
        base: 'flex',
        '@trade-page-body/desktop': 'none',
      }}
      css={commonStyle}
      pt={0}
      data-cy="StatSection"
    >
      {!isOpen ? (
        <StatSectionSummary onExpand={() => setIsOpen(true)} />
      ) : (
        <>
          <Flex gap="16px" alignItems="center" justifyContent="space-between">
            <BorrowRates
              ml={{
                base: '14px',
                mobile: '32px',
              }}
            />
            <Icon onClick={() => setIsOpen(false)} cursor="pointer">
              <ArrowUp />
            </Icon>
          </Flex>
          <Divider mt={3} mb={2} />
          <CollapseStats />
        </>
      )}
    </Flex>
  );
};

/**
 * V2 MarketBar (56px): [pair selector ⌄] price · signed change │ stat cells
 * divided by 1px borders. Price is white mono; only the change % is coloured.
 */
const Desktop = () => {
  const { value: isContainerDesktop, isPending } =
    useIsTradePageContainerDesktop();

  if (!isPending && !isContainerDesktop) {
    return <></>;
  }

  return (
    <Flex
      borderBottom="1px solid"
      borderBottomColor="v2.border.default"
      bg="v2.bg.panel"
      h="layout.pairInfoBarHeight"
      alignItems="center"
      px="8px"
      overflowX="auto"
      className={css({
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      })}
    >
      <PairPopover>
        <HStack
          _hover={{
            cursor: 'pointer',
            background: 'v2.bg.surface1',
            borderColor: 'v2.border.default',
          }}
          border="1px solid transparent"
          borderRadius="4px"
          h="40px"
          px="12px"
          gap="10px"
          flexShrink={0}
          transition="all 120ms"
        >
          <TokenPair
            display={{
              base: 'none',
              desktop: 'flex',
            }}
          />
          <Icon color="v2.text.subtle" boxSize="14px">
            <ArrowDown />
          </Icon>
        </HStack>
      </PairPopover>
      <HStack gap="10px" px="16px" flexShrink={0}>
        <TokenPrice />
        <DayStats />
      </HStack>
      <Flex
        display={{
          base: 'none',
          '@trade-page-body/desktop': 'flex',
        }}
        alignItems="stretch"
        data-cy="StatSection"
        h="full"
        // narrow desktop: the stat strip pans inside itself (brief §4.1
        // tablet rule) — it must never widen the page into an H-scrollbar
        minW={0}
        overflowX="auto"
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <StatCell>
          <Vol24h />
        </StatCell>
        <StatCell minW="290px">
          <OpenInterestsDesktop gap={5} />
        </StatCell>
        <StatCell>
          <BorrowRates />
        </StatCell>
      </Flex>
    </Flex>
  );
};

/** V2 stat cluster cell: 1px left border divider, centered column */
const StatCell = ({ children, ...p }: FlexProps) => (
  <Flex
    px="16px"
    borderLeft="1px solid"
    borderLeftColor="v2.border.default"
    flexDir="column"
    justifyContent="center"
    whiteSpace="nowrap"
    flexShrink={0}
    {...p}
  >
    {children}
  </Flex>
);

const commonStyle = css.raw({
  px: { base: 4, '@trade-page-body/desktop': 12 },
  py: { base: 0, '@trade-page-body/desktop': 3 },
  flexDir: {
    base: 'column',
    '@trade-page-body/desktop': 'row',
  },
});
